import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Checkbox, TextInput, TileGroup, Toggle } from "@opendate-app/setlist";

const HOUR_FIELDS = [
  "daily_overtime_after_minutes",
  "daily_double_time_after_minutes",
  "weekly_overtime_after_minutes",
  "meal_break_after_minutes",
  "rest_break_per_minutes",
];

const PAYLOAD_FIELDS = [
  "preset",
  "daily_overtime_after_minutes",
  "daily_double_time_after_minutes",
  "weekly_overtime_after_minutes",
  "overtime_multiplier",
  "double_time_multiplier",
  "seventh_day_rule",
  "meal_break_after_minutes",
  "meal_break_minutes",
  "rest_break_per_minutes",
  "early_clock_in_minutes",
  "auto_clock_out_after_minutes",
  "geofence_required",
  "geofence_radius_meters",
  "weekly_labor_budget",
  "event_labor_budget",
];

const CUSTOM_PRESET = "custom";

const sectionStyle = { borderTop: "1px solid var(--sl-border-secondary)", paddingTop: 20, marginTop: 20 };
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 16, marginBottom: 16 };

const blank = (value) => value === null || value === undefined || value === "";

const hoursText = (minutes) => (blank(minutes) ? "" : String(Number(minutes) / 60));

const hourTextsFor = (ruleSet) => Object.fromEntries(HOUR_FIELDS.map((field) => [field, hoursText(ruleSet[field])]));

const hoursLabel = (minutes) => {
  if (blank(minutes)) return null;
  const hours = Number(minutes) / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} h`;
};

export const laborRuleSetPayload = (ruleSet) => Object.fromEntries(PAYLOAD_FIELDS.map((field) => [field, ruleSet[field] ?? null]));

const SectionHeading = ({ title, subtitle }) => (
  <div style={{ marginBottom: 12 }}>
    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--sl-text-primary)" }}>
      {title}
    </h4>
    {subtitle && (
      <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--sl-text-tertiary)" }}>
        {subtitle}
      </p>
    )}
  </div>
);

SectionHeading.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

const LaborRuleSetForm = ({ idPrefix, ruleSet, presets, presetOptions, budgetScope, onSave, saveLabel, secondaryAction }) => {
  const [form, setForm] = useState(ruleSet);
  const [hourTexts, setHourTexts] = useState(() => hourTextsFor(ruleSet));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const custom = form.preset === CUSTOM_PRESET;
  const presetLabel = presetOptions.find((option) => option.value === form.preset)?.label;

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const choosePreset = (preset) => {
    const next = { ...form, ...(presets[preset] || {}), preset };
    setForm(next);
    setHourTexts(hourTextsFor(next));
  };

  const setHours = (key, text) => {
    setHourTexts((current) => ({ ...current, [key]: text }));
    setField(key, text === "" ? null : Math.round(Number(text) * 60));
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const saved = await onSave(laborRuleSetPayload(form));
      setForm(saved);
      setHourTexts(hourTextsFor(saved));
    } catch (error) {
      setErrors(error?.response?.data?.errors || ["Could not save labor rules."]);
    } finally {
      setSaving(false);
    }
  };

  const hoursInput = (key, label, props = {}) => (
    <TextInput
      id={`${idPrefix}-${key}`}
      label={label}
      type="number"
      min={0}
      step={0.5}
      trailingText="h"
      placeholder="Off"
      value={hourTexts[key]}
      onChange={(event) => setHours(key, event.target.value)}
      {...props}
    />
  );

  const numberInput = (key, label, props = {}) => (
    <TextInput id={`${idPrefix}-${key}`} label={label} type="number" value={form[key] ?? ""} onChange={(event) => setField(key, event.target.value)} {...props} />
  );

  const ruleProps = { disabled: !custom };
  const showDoubleTimeRate = custom || !blank(form.double_time_multiplier);
  const showRestBreak = custom || !blank(form.rest_break_per_minutes);
  const showSeventhDay = custom || Boolean(form.seventh_day_rule);
  const autoClockOutHours = hoursLabel(form.auto_clock_out_after_minutes);

  return (
    <div>
      {errors.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="error" title="Could not save labor rules" description={errors.join(" ")} />
        </div>
      )}

      <TileGroup
        name={`${idPrefix}-preset`}
        columns={3}
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
        value={form.preset}
        onChange={choosePreset}
        items={presetOptions.map((option) => ({ value: option.value, label: option.label, supportingText: option.description }))}
      />

      <div style={sectionStyle}>
        <SectionHeading
          title="Overtime and breaks"
          subtitle={custom ? "Set each rule for your team. Leave a rule blank to turn it off." : `Set by the ${presetLabel} preset. Pick Custom to change them.`}
        />
        <div style={gridStyle}>
          {hoursInput("weekly_overtime_after_minutes", "Weekly OT after", ruleProps)}
          {hoursInput("daily_overtime_after_minutes", "Daily OT after", ruleProps)}
          {hoursInput("daily_double_time_after_minutes", "Daily DT after", ruleProps)}
          {numberInput("overtime_multiplier", "OT multiplier", { min: 1, step: 0.05, trailingText: "×", ...ruleProps })}
          {hoursInput("meal_break_after_minutes", "Meal break required after", ruleProps)}
          {numberInput("meal_break_minutes", "Meal break length", { min: 1, trailingText: "min", placeholder: "Off", ...ruleProps })}
          {showRestBreak && hoursInput("rest_break_per_minutes", "Rest break every", ruleProps)}
          {showDoubleTimeRate && numberInput("double_time_multiplier", "DT multiplier", { min: 1, step: 0.05, trailingText: "×", ...ruleProps })}
        </div>
        {showSeventhDay && (
          <Checkbox
            id={`${idPrefix}-seventh_day_rule`}
            label="Seventh day in a row: the first 8 h are OT and the rest are DT"
            disabled={!custom}
            checked={Boolean(form.seventh_day_rule)}
            onChange={(event) => setField("seventh_day_rule", event.target.checked)}
          />
        )}
      </div>

      <div style={sectionStyle}>
        <SectionHeading title="Time clock" />
        <div style={gridStyle}>
          {numberInput("early_clock_in_minutes", "Early clock-in window", { min: 0, trailingText: "min", hint: "No clock-ins earlier than this before a shift" })}
          {numberInput("auto_clock_out_after_minutes", "Auto clock-out after", {
            min: 1,
            trailingText: "min",
            hint: autoClockOutHours ? `Closes and flags forgotten cards. That's ${autoClockOutHours}.` : "Closes and flags forgotten cards",
          })}
          {numberInput("geofence_radius_meters", "Geofence radius", { min: 1, trailingText: "m", hint: "Measured from the venue address" })}
        </div>
        <Toggle
          id={`${idPrefix}-geofence_required`}
          type="description"
          label="Require location to clock in from a phone"
          description="Kiosk punches skip this check."
          checked={Boolean(form.geofence_required)}
          onChange={(event) => setField("geofence_required", event.target.checked)}
        />
      </div>

      <div style={sectionStyle}>
        <div style={{ ...gridStyle, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 340px))" }}>
          {numberInput("weekly_labor_budget", "Weekly labor budget", {
            min: 0,
            step: 1,
            inputMode: "numeric",
            leadingText: "$",
            hint: `${budgetScope}. The schedule footer turns amber over 100%.`,
          })}
          {numberInput("event_labor_budget", "Event labor budget", {
            min: 0,
            step: 1,
            inputMode: "numeric",
            leadingText: "$",
            hint: "Per show. Event staffing and Labor by event turn amber over it.",
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : saveLabel}
        </Button>
        {secondaryAction}
      </div>
    </div>
  );
};

LaborRuleSetForm.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  ruleSet: PropTypes.object.isRequired,
  presets: PropTypes.object.isRequired,
  presetOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, description: PropTypes.string })).isRequired,
  budgetScope: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  saveLabel: PropTypes.string.isRequired,
  secondaryAction: PropTypes.node,
};

export default LaborRuleSetForm;
